import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Input from '../../components/Input';
import { secureGet, securePut, securePost } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import SnackbarNotification from '../../components/SnackbarNotification';

const AppointmentForm = ({ appointment, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    clientId: '',
    vehicleId: '',
    appointmentDate: '',
    description: '',
  });
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };
  useEffect(() => {
    // Fetch clients for dropdown
    const fetchClients = async () => {
      try {
        const response = await secureGet('/clients', () => {});
        const clientOptions = response.data.map(client => ({
          value: client.id,
          reference: client.code,
          name: client.name,
        }));
        setClients(clientOptions);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };
    fetchClients();

    // Pre-fill form if editing
    if (appointment) {
      setFormData({
        id: appointment.id,
        title: appointment.title || '',
        clientId: appointment.clientId || '',
        vehicleId: appointment.vehicleId || '',
        appointmentDate: appointment.appointmentDate 
          ? moment(appointment.appointmentDate).format('YYYY-MM-DDTHH:mm')
          : appointment.start 
            ? moment(appointment.start).format('YYYY-MM-DDTHH:mm')
            : '',
        description: appointment.description || '',
      });
      
      // Fetch vehicles for the client if clientId exists
      if (appointment.clientId) {
        fetchVehiclesForClient(appointment.clientId);
      }
    }
  }, [appointment]);

  // Fetch vehicles for selected client
  const fetchVehiclesForClient = async (clientId) => {
    if (!clientId) {
      setVehicles([]);
      return;
    }
    
    try {
      const response = await secureGet(`/vehicles/client/${clientId}`, () => {});
      const vehicleOptions = response.data.map(vehicle => ({
        value: vehicle.id,
        label: `${vehicle.type || ''} - ${vehicle.brandMake || ''} (${vehicle.immatriculationNumber || ''})`,
        reference: `${vehicle.immatriculationNumber} - ${vehicle.color || ''}`,
        name: `${t(`filters.vehicles.${vehicle.type}`)} - ${vehicle.brand?.name || ''}`.trim(),
      }));
      setVehicles(vehicleOptions);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // If client changed, fetch vehicles and clear vehicle selection
    if (name === 'clientId') {
      fetchVehiclesForClient(value);
      setFormData(prev => ({ ...prev, vehicleId: '' }));
    }
  };

  const formatDateWithTimezone = (dateString) => {
    if (!dateString) return "";
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    
    const timezoneOffset = -date.getTimezoneOffset();
    const absOffset = Math.abs(timezoneOffset);
    const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0");
    const offsetMinutes = String(absOffset % 60).padStart(2, "0");
    const timezone = `${timezoneOffset >= 0 ? "+" : "-"}${offsetHours}${offsetMinutes}`;

    return `${year}-${month}-${day}T${hours}:${minutes}${timezone}`;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!formData.title || !formData.clientId || !formData.vehicleId || !formData.appointmentDate) {
      showSnackbar('Veuillez remplir tous les champs obligatoires', 'error');
      return;
    }
    
    setLoading(true);

    try {
      const appointmentData = {
        ...(formData.id && { id: formData.id }),
        title: formData.title,
        clientId: formData.clientId ? Number(formData.clientId) : 0,
        vehicleId: formData.vehicleId ? Number(formData.vehicleId) : 0,
        appointmentDate: formatDateWithTimezone(formData.appointmentDate),
        description: formData.description,
      };
      if (appointment?.id) {
        await securePut(`/appointments/${appointment.id}`, appointmentData, () => {});
      } else {
        await securePost('/appointments/add', appointmentData, () => {});
      }
      
      if (onSave) {
        onSave(appointmentData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving appointment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-[16px] w-[700px] max-h-[90vh] overflow-y-auto p-8 relative shadow-lg z-[10000]">
        {/* Header */}
        <h2 className="text-[32px] text-[#38485C] font-semibold mb-6">
          {appointment?.id ? "Modifier un rendez-vous" : "Nouveau rendez-vous"}
        </h2>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Row 1: Title and Client */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="text"
              label="Titre"
              name="title"
              value={formData.title || ''}
              onChange={(value) => handleChange('title', value)}
              required
            />

            <Input
              type="dropdown"
              label="Client"
              name="clientId"
              value={formData.clientId || ''}
              onChange={(value) => handleChange('clientId', value)}
              options={clients}
              required
            />
          </div>

          {/* Row 2: Vehicle and Date/Time */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="dropdown"
              label="Véhicule"
              name="vehicleId"
              value={formData.vehicleId || ''}
              onChange={(value) => handleChange('vehicleId', value)}
              options={vehicles}
              required
            />

            <Input
              type="datetime-local"
              label="Date Et Heure"
              name="appointmentDate"
              value={formData.appointmentDate || ''}
              onChange={(value) => handleChange('appointmentDate', value)}
              required
            />
          </div>

          {/* Row 3: Description (full width) */}
          <textarea
            name="description"
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Description"
            rows={4}
            className="w-full px-4 py-3 border border-[#E3E3E3] rounded-lg focus:border-primary focus:outline-none text-[16px] font-medium bg-white text-[#000000] resize-none"
          />

          {/* Actions */}
          <div className="flex justify-end mt-6">
            <SaveCancel
              onCancelClick={onClose}
              onSaveClick={handleSubmit}
              loading={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;

